import { z } from 'zod';
import { IdSchema, InstantSchema, LocalDateSchema, MoneySchema } from '../common/index.js';
import * as E from '../entities/index.js';

/**
 * Schémas des outils du serveur MCP « lakou » (expert 1, §6.2). Le serveur (L40) n'accède jamais à la base :
 * il appelle les routes du contrat avec le jeton d'un utilisateur réel. Toute écriture se fait en deux temps :
 * l'outil renvoie un aperçu et un jeton signé, puis `lakou_confirm` exécute.
 */

export const ApercuConfirmationSchema = z
  .object({
    preview: z.string().min(1),
    confirmation_token: z.string().min(32),
    expiresAt: InstantSchema,
  })
  .meta({ id: 'McpConfirmationPreview', description: 'Aperçu lisible et jeton de confirmation (HMAC, 5 minutes, usage unique).' });

export interface OutilMcp {
  nom: string;
  description: string;
  confirmation: boolean;
  annotations: { readOnlyHint: boolean; destructiveHint: boolean };
  entree: z.ZodObject;
  sortie: z.ZodType;
  routes: readonly string[];
}

const LECTURE = { readOnlyHint: true, destructiveHint: false } as const;
const ECRITURE = { readOnlyHint: false, destructiveHint: false } as const;

const lecture = (nom: string, description: string, entree: z.ZodObject, sortie: z.ZodType, routes: string[]): OutilMcp => ({
  nom,
  description,
  confirmation: false,
  annotations: LECTURE,
  entree,
  sortie,
  routes,
});

const ecriture = (nom: string, description: string, entree: z.ZodObject, routes: string[], destructif = false): OutilMcp => ({
  nom,
  description,
  confirmation: true,
  annotations: { ...ECRITURE, destructiveHint: destructif },
  entree,
  sortie: ApercuConfirmationSchema,
  routes,
});

const dossier = z.object({ dossierId: IdSchema });
const vide = z.object({});

export const OUTILS_MCP = [
  lecture(
    'lakou_list_dossiers',
    'Liste les dossiers, filtrés par phase, blocage ou étude.',
    z.object({ phase: E.PhaseSchema.optional(), bloque: z.boolean().optional(), etudeId: IdSchema.optional() }),
    z.array(E.DossierSummarySchema),
    ['dossiers.lister'],
  ),
  lecture(
    'lakou_get_dossier',
    'Vue d’ensemble d’un dossier : étapes, blocages, prochaines actions, délais légaux.',
    dossier,
    E.DossierStaffViewSchema,
    ['dossiers.lire'],
  ),
  lecture(
    'lakou_search_persons',
    'Recherche floue de personnes dans un dossier ou dans tous.',
    z.object({ q: z.string().min(2), dossierId: IdSchema.optional() }),
    z.array(E.PersonSummarySchema),
    ['genealogie.personnes.rechercher'],
  ),
  lecture(
    'lakou_get_heir',
    'Fiche d’un héritier, masquée selon les droits de l’utilisateur.',
    z.object({ personId: IdSchema }),
    E.PersonStaffViewSchema,
    ['genealogie.personne.lire'],
  ),
  lecture('lakou_get_tree', 'Arbre et parts calculées avec la trace d’explication.', dossier, E.FamilyTreeSchema, ['genealogie.arbre']),
  lecture('lakou_list_tasks', 'File du jour d’un utilisateur.', z.object({ jour: LocalDateSchema.optional() }), z.array(E.TaskSchema), [
    'taches.fileDuJour',
  ]),
  lecture(
    'lakou_list_followups',
    'Relances programmées, en pause ou en attente de validation.',
    z.object({ dossierId: IdSchema.optional() }),
    z.array(E.FollowupPlanSchema),
    ['relances.plans'],
  ),
  lecture('lakou_list_unread_messages', 'Messages non lus et fils non rattachés de la boîte unifiée.', vide, z.array(E.ThreadSchema), [
    'messagerie.fils',
  ]),
  lecture(
    'lakou_billing_summary',
    'Facturé, encaissé et reste dû d’un dossier.',
    dossier,
    z.object({ collected: MoneySchema, outstanding: MoneySchema }),
    ['facturation.dossier'],
  ),
  lecture('lakou_list_blocking_unpaid', 'Héritiers dont l’impayé bloque une signature.', dossier, z.array(E.HeirBillingLineSchema), [
    'facturation.dossier',
    'facturation.collecte',
  ]),
  lecture(
    'lakou_get_deadlines',
    'Échéances légales à venir.',
    z.object({ dossierId: IdSchema.optional(), avant: LocalDateSchema.optional() }),
    z.array(E.DeadlineSchema),
    ['echeances.lister'],
  ),
  lecture(
    'lakou_list_beta_questions',
    'Questions BETA en attente pour l’utilisateur.',
    z.object({ contexte: z.string().min(2) }),
    z.array(E.BetaQuestionViewSchema),
    ['beta.questionsDues'],
  ),
  lecture('lakou_get_learned_settings', 'Réglages appris par les réponses BETA.', vide, z.array(E.LearnedSettingSchema), ['beta.reglages']),
  lecture(
    'lakou_export_timeline',
    'Journal horodaté d’un dossier ; un export externe exclut toujours les coulisses.',
    z.object({ dossierId: IdSchema, destinataire: z.enum(['equipe', 'notaire', 'juge']) }),
    E.ExportSchema,
    ['dossiers.journal.exporter'],
  ),
  ecriture(
    'lakou_draft_message',
    'Prépare un brouillon de message, sans jamais l’envoyer.',
    z.object({
      dossierId: IdSchema,
      personId: IdSchema,
      templateCode: z
        .string()
        .regex(/^[A-Z]\d$/)
        .nullable(),
      consigne: z.string().min(3),
    }),
    ['messagerie.envoyer'],
  ),
  ecriture(
    'lakou_schedule_message',
    'Programme un message (contrôles de cadence, consentement et créneaux appliqués par l’API).',
    z.object({ dossierId: IdSchema, personId: IdSchema, channelAccountId: IdSchema, body: z.string().min(1), scheduledAt: InstantSchema }),
    ['messagerie.envoyer'],
  ),
  ecriture(
    'lakou_pause_followups',
    'Met en pause les relances d’une personne (« on laisse respirer »).',
    z.object({ planId: IdSchema, reason: z.string().min(3), until: LocalDateSchema.nullable() }),
    ['relances.pause'],
  ),
  ecriture('lakou_resume_followups', 'Reprend les relances d’une personne.', z.object({ planId: IdSchema }), ['relances.reprise']),
  ecriture('lakou_create_task', 'Crée une tâche dans la file du jour.', E.TaskCreateSchema, ['taches.creer']),
  ecriture('lakou_answer_decision', 'Tranche une décision en attente.', z.object({ decisionId: IdSchema, option: z.string().min(1) }), [
    'decisions.trancher',
  ]),
  ecriture(
    'lakou_set_installment_plan',
    'Met un héritier en échéancier (montants confirmés avant exécution).',
    z.object({ dossierId: IdSchema, personId: IdSchema, total: MoneySchema, count: z.number().int().min(1).max(12) }),
    ['facturation.echeancier'],
  ),
  ecriture(
    'lakou_apply_waiver',
    'Applique une remise décidée par Isabelle.',
    z.object({ dossierId: IdSchema, personId: IdSchema, amount: MoneySchema, reason: z.string().min(3) }),
    ['facturation.regles'],
  ),
  ecriture(
    'lakou_toggle_billing_rule',
    'Coche ou décoche une règle de facturation (nouvelle version du jeu de règles).',
    z.object({ dossierId: IdSchema, ruleCode: E.BillingRuleItemSchema.shape.ruleCode, enabled: z.boolean() }),
    ['facturation.regles'],
  ),
  ecriture(
    'lakou_send_payment_links',
    'Crée et envoie des liens de paiement.',
    z.object({ dossierId: IdSchema, installmentIds: z.array(IdSchema).min(1), provider: z.enum(['stripe', 'gocardless']) }),
    ['paiements.liens'],
  ),
  ecriture(
    'lakou_answer_beta',
    'Répond à une question BETA.',
    z.object({ cle: z.string().min(2), option: z.string().min(1), comment: z.string().optional() }),
    ['beta.repondre'],
  ),
  ecriture(
    'lakou_add_note',
    'Ajoute une note interne (coulisses).',
    z.object({ dossierId: IdSchema, personId: IdSchema.nullable(), body: z.string().min(1) }),
    ['confidentialite.notes.ajouter'],
  ),
  {
    nom: 'lakou_confirm',
    description: 'Exécute une écriture préparée, sur présentation de son jeton de confirmation.',
    confirmation: false,
    annotations: { readOnlyHint: false, destructiveHint: true },
    entree: z.object({ confirmation_token: z.string().min(32) }),
    sortie: z.object({ executed: z.literal(true), summary: z.string().min(1) }),
    routes: [],
  },
] as const satisfies readonly OutilMcp[];

export type NomOutilMcp = (typeof OUTILS_MCP)[number]['nom'];

export function outilMcp(nom: NomOutilMcp): OutilMcp {
  const o = OUTILS_MCP.find((x) => x.nom === nom);
  if (!o) throw new Error(`Outil MCP inconnu : ${nom}`);
  return o;
}
